import type { Translate } from "@/lib/i18n/translate";
import { ld } from "@/lib/theme/light-dark";
/**
 * Static data for the Kalender hero module — 1:1 port of the prototype's
 * `kalData()` / `buildMonth()` builders (Hausverwaltung Hero.dc.html, lines
 * 2294–2358). Kept in a sibling file so `module-kalender.tsx` stays under the
 * line budget. The data is fixed (June 2026), so we precompute it here instead
 * of re-deriving from `new Date()`.
 */
import { H } from "../../hero-tokens";

/** Per-hour pixel height of the week grid (prototype `H = 42`). */
export const HOUR_H = 42;
const BASE_HOUR = 8;
const END_HOUR = 19;

/** Total height of the scrollable week grid: (19 - 8) * 42 = 462px. */
export const GRID_HEIGHT = (END_HOUR - BASE_HOUR) * HOUR_H;

export interface MiniDay {
  readonly day: number;
  readonly bg: string;
  readonly color: string;
  readonly weight: string;
}

export interface CalEvent {
  readonly top: number;
  readonly height: number;
  readonly title: string;
  readonly time: string;
  readonly bg: string;
  readonly fg: string;
  readonly bar: string;
}

export interface WeekDay {
  readonly letter: string;
  readonly date: string;
  readonly isToday: boolean;
  readonly dayColor: string;
  readonly dateBg: string;
  readonly dateColor: string;
  readonly colBg: string;
  readonly events: readonly CalEvent[];
}

function hexToRgba(hex: string, a: number): string {
  const n = hex.replace("#", "");
  const r = Number.parseInt(n.slice(0, 2), 16);
  const g = Number.parseInt(n.slice(2, 4), 16);
  const b = Number.parseInt(n.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${a})`;
}

/** Build the 42-cell month grid for June 2026 (month index 5), Monday-first. */
function buildMonth(
  year: number,
  month: number,
): readonly {
  readonly day: number;
  readonly inMonth: boolean;
  readonly col: number;
}[] {
  const startDow = (new Date(year, month, 1).getDay() + 6) % 7;
  const dim = new Date(year, month + 1, 0).getDate();
  const prevDim = new Date(year, month, 0).getDate();
  const cells: { day: number; inMonth: boolean; col: number }[] = [];
  for (let i = 0; i < 42; i++) {
    const dn = i - startDow + 1;
    let day: number;
    let inMonth: boolean;
    if (dn < 1) {
      day = prevDim + dn;
      inMonth = false;
    } else if (dn > dim) {
      day = dn - dim;
      inMonth = false;
    } else {
      day = dn;
      inMonth = true;
    }
    cells.push({ day, inMonth, col: i % 7 });
  }
  return cells;
}

function chunk<T>(arr: readonly T[]): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += 7) out.push(arr.slice(i, i + 7));
  return out;
}

const MONTH_CELLS = buildMonth(2026, 5);

/** Mini-month for the sidebar: 5 weeks, day 27 = today (dark). */
export const MINI_WEEKS: readonly (readonly MiniDay[])[] = chunk(
  MONTH_CELLS.map((c): MiniDay => {
    const isToday = c.inMonth && c.day === 27;
    return {
      day: c.day,
      bg: isToday ? ld("#0B0F19", "#fafafa") : "transparent",
      color: isToday
        ? ld("#ffffff", "#181b1f")
        : c.inMonth
          ? ld("rgba(11,15,25,0.72)", "rgba(250,250,250,0.72)")
          : ld("rgba(11,15,25,0.26)", "rgba(250,250,250,0.26)"),
      weight: isToday ? "700" : "500",
    };
  }),
).slice(0, 5);

/** Hour labels down the gutter: 08:00 … 19:00 (12 entries). */
export const HOURS: readonly string[] = Array.from(
  { length: END_HOUR - BASE_HOUR + 1 },
  (_, i) => {
    const h = BASE_HOUR + i;
    return `${h < 10 ? "0" : ""}${h}:00`;
  },
);

const TEAL = { bg: hexToRgba(H.accent, 0.13), fg: ld("#00564B", "#95dccd"), bar: H.accent };
const AMBER = { bg: ld("#F6ECD6", "#3a311d"), fg: ld("#8A6516", "#cca45c"), bar: "#B7791F" };
const BLUE = { bg: ld("#E4EEF1", "#303337"), fg: ld("#2C5F6E", "#95c9da"), bar: "#3F7E91" };

function fmt(h: number): string {
  const hh = Math.floor(h);
  const mm = Math.round((h - hh) * 60);
  return `${hh < 10 ? "0" : ""}${hh}:${mm === 0 ? "00" : mm}`;
}

function ev(
  s: number,
  e: number,
  title: string,
  c: { readonly bg: string; readonly fg: string; readonly bar: string },
): CalEvent {
  return {
    top: (s - BASE_HOUR) * HOUR_H,
    height: Math.max((e - s) * HOUR_H - 4, 24),
    title,
    time: `${fmt(s)}–${fmt(e)}`,
    bg: c.bg,
    fg: c.fg,
    bar: c.bar,
  };
}

interface RawDay {
  readonly letter: string;
  readonly date: string;
  readonly isToday: boolean;
  readonly events: readonly CalEvent[];
}

function rawDays(t: Translate): readonly RawDay[] {
  return [
    {
      letter: "Mo",
      date: "22",
      isToday: false,
      events: [ev(10, 11, "Objektbegehung Ahornweg 5", TEAL)],
    },
    {
      letter: "Di",
      date: "23",
      isToday: false,
      events: [ev(17, 18.5, "ETV Lindenhof", AMBER)],
    },
    {
      letter: "Mi",
      date: "24",
      isToday: false,
      events: [ev(14, 15, t("wohnungsuebergabeWe12"), TEAL)],
    },
    {
      letter: "Do",
      date: "25",
      isToday: false,
      events: [
        ev(9, 10, t("handwerkerHeizung"), TEAL),
        ev(15, 16, "Mietersprechstunde", TEAL),
      ],
    },
    {
      letter: "Fr",
      date: "26",
      isToday: false,
      events: [
        ev(11, 12, "Begehung Birkenallee", TEAL),
        ev(13, 14, "Onboarding hausverwalter.ai", BLUE),
      ],
    },
    {
      letter: "Sa",
      date: "27",
      isToday: true,
      events: [ev(10, 11, "Wohnungsbesichtigung", BLUE)],
    },
    { letter: "So", date: "28", isToday: false, events: [] },
  ];
}

export function weekDays(t: Translate): readonly WeekDay[] {
  return rawDays(t).map(
    (d): WeekDay => ({
      ...d,
      dayColor: d.isToday ? H.accent : ld("rgba(11,15,25,0.5)", "rgba(250,250,250,0.5)"),
      dateBg: d.isToday ? H.accent : "transparent",
      dateColor: d.isToday ? ld("#ffffff", "#181b1f") : ld("#0B0F19", "#fafafa"),
      colBg: d.isToday ? hexToRgba(H.accent, 0.035) : "transparent",
    }),
  );
}
